import React from 'react'
import PropTypes from 'prop-types'
import {TextInput as PepsicoDsTextInput} from '@pepsico-ds/ui'

/**
 * TextInput component that renders a PepsicoDsTextInput with properties from props.
 *
 * @param {Object} props - The properties object.
 * @param {string|number} props.value - The value of the text input.
 * @param {Function} props.onChange - The function to handle input change events.
 * @param {string} props.label - The label text for the input.
 * @param {boolean} [props.required=false] - Optional boolean to indicate if the input is required.
 * @param {boolean} [props.disabled=false] - Optional boolean to disable the input.
 * @param {number} [props.maxLength] - Optional maximum length of the input value.
 * @param {string} [props.type='text'] - Optional type of the input.
 * @param {string} [props.error] - Optional error message to display.
 * @param {boolean} [props.isDark=false] - Optional boolean to indicate dark mode styling for the input.
 * @param {string} [props.placeholderText=''] - Optional placeholder text for the input.
 * @param {string} [props.helperText] - Optional helper text to display below the input.
 * @param {Function} [props.onBlur] - Optional function to handle input blur events.
 * @param {string} [props.className=''] - Optional additional CSS class names to apply to the input.
 * @param {string} [props.id=''] - Optional additional id to apply to the input.
 * @param {Function} props.handleKeyDown - Block decimal & non-integer keys.
 * @returns {JSX.Element} The rendered PepsicoDsTextInput component.
 */

const TextInput = ({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  maxLength,
  type = 'text',
  error,
  isDark = false,
  placeholderText = '',
  helperText = '',
  onBlur,
  className = '',
  id,
  handleKeyDown,
}) => {
  return (
    <PepsicoDsTextInput
      data-testid={`text-input` + (id ? `_${id}` : '')}
      id={`text-input` + (id ? `_${id}` : '')}
      value={value}
      onUpdate={onChange}
      label={label}
      required={required}
      disabled={disabled}
      maxLength={maxLength}
      type={type}
      error={error}
      isDark={isDark}
      placeholderText={placeholderText}
      helperText={helperText}
      onBlur={onBlur}
      className={`custom-text-input ${className}`}
      onKeyDown={handleKeyDown} // Block decimal & non-integer keys
    />
  )
}

TextInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  type: PropTypes.string,
  error: PropTypes.string,
  isDark: PropTypes.bool,
  placeholderText: PropTypes.string,
  helperText: PropTypes.string,
  onBlur: PropTypes.func,
  className: PropTypes.string,
  id: PropTypes.string,
  handleKeyDown: PropTypes.func,
}

export default TextInput
